import { useState } from "react";
import { CheckCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "@/components/ui/sonner";
import { ConfirmDialog } from "@/components/shared/ConfirmDialog";
import { HtmlSeguro } from "@/components/shared/HtmlSeguro";
import { formatDateHoraBr } from "@/lib/format";
import { useDismissNotificacao, useDismissTodas, useNotificacoes, type Notificacao } from "./api";

// Mesmos tamanhos do legado: não lidas crescem de 10 em 10, histórico de 100 em 100.
const PASSO_NAO_LIDAS = 10;
const PASSO_HISTORICO = 100;

function ItemNotificacao({ notificacao, onClick }: { notificacao: Notificacao; onClick?: () => void }) {
  const conteudo = (
    <>
      <p className="text-xs text-muted-foreground">{formatDateHoraBr(notificacao.dataHoraInclusao)}</p>
      <h3 className="mt-0.5 text-sm font-semibold text-foreground">{notificacao.tituloPortal}</h3>
      <HtmlSeguro html={notificacao.corpoPortal} className="mt-1 text-sm text-muted-foreground" />
    </>
  );
  if (!onClick) return <div className="rounded-lg border border-border p-4">{conteudo}</div>;
  return (
    <button
      type="button"
      onClick={onClick}
      className="group flex w-full items-start justify-between gap-4 rounded-lg border border-border p-4 text-left transition-colors hover:bg-muted/50"
    >
      <div className="min-w-0">{conteudo}</div>
      <span className="shrink-0 text-xs font-medium text-primary opacity-70 group-hover:opacity-100">Marcar como lida</span>
    </button>
  );
}

function MostrarMais({ ultima, onClick, carregando, textoFim }: { ultima: boolean; onClick: () => void; carregando: boolean; textoFim: string }) {
  return (
    <div className="flex justify-center pt-2">
      {ultima ? (
        <p className="text-sm text-muted-foreground">{textoFim}</p>
      ) : (
        <Button variant="ghost" onClick={onClick} disabled={carregando}>
          Mostrar mais notificações
        </Button>
      )}
    </div>
  );
}

export default function ConsultaNotificacao() {
  const [sizeNovas, setSizeNovas] = useState(PASSO_NAO_LIDAS);
  const [sizeHistorico, setSizeHistorico] = useState(PASSO_HISTORICO);
  const [confirmandoTodas, setConfirmandoTodas] = useState(false);

  const novas = useNotificacoes("nao-lidas", sizeNovas);
  const historico = useNotificacoes("historico", sizeHistorico);
  const dismiss = useDismissNotificacao();
  const dismissTodas = useDismissTodas();

  const totalNovas = novas.data?.totalElements ?? 0;

  return (
    <section className="rounded-xl border border-border bg-card p-5 shadow-card">
      <Tabs defaultValue="nao-lidas">
        <TabsList>
          <TabsTrigger value="nao-lidas">Não lidas{novas.data ? ` (${totalNovas})` : ""}</TabsTrigger>
          <TabsTrigger value="historico">Histórico</TabsTrigger>
        </TabsList>

        <TabsContent value="nao-lidas" className="space-y-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <p className="text-sm font-medium text-foreground">
              {novas.isLoading
                ? "Carregando…"
                : totalNovas > 0
                  ? `Você tem ${totalNovas} novas notificações`
                  : "Você não tem novas notificações"}
            </p>
            <Button variant="outline" size="sm" onClick={() => setConfirmandoTodas(true)} disabled={totalNovas === 0 || dismissTodas.isPending}>
              <CheckCheck className="mr-1.5 h-4 w-4" />
              Marcar todas como lidas
            </Button>
          </div>

          {novas.isLoading ? (
            <Skeleton className="h-32 w-full" />
          ) : (
            <>
              {novas.data?.content.map((n) => (
                <ItemNotificacao key={n.id} notificacao={n} onClick={() => dismiss.mutate(n.id)} />
              ))}
              {totalNovas > 0 && (
                <MostrarMais
                  ultima={novas.data?.last ?? true}
                  carregando={novas.isFetching}
                  onClick={() => setSizeNovas((s) => s + PASSO_NAO_LIDAS)}
                  textoFim="Última notificação — sem notificações mais antigas para exibir"
                />
              )}
            </>
          )}
        </TabsContent>

        <TabsContent value="historico" className="space-y-3">
          {historico.isLoading ? (
            <Skeleton className="h-32 w-full" />
          ) : historico.data?.totalElements === 0 ? (
            <p className="text-sm text-muted-foreground">Você não tem notificações</p>
          ) : (
            <>
              {historico.data?.content.map((n) => (
                <ItemNotificacao key={n.id} notificacao={n} />
              ))}
              <MostrarMais
                ultima={historico.data?.last ?? true}
                carregando={historico.isFetching}
                onClick={() => setSizeHistorico((s) => s + PASSO_HISTORICO)}
                textoFim="Última notificação — sem notificações mais antigas para exibir"
              />
            </>
          )}
        </TabsContent>
      </Tabs>

      <ConfirmDialog
        open={confirmandoTodas}
        onOpenChange={setConfirmandoTodas}
        title="Marcar todas como lidas?"
        description="Todas as notificações não lidas vão para o histórico."
        confirmLabel="Marcar todas"
        onConfirm={() => dismissTodas.mutate(undefined, { onSuccess: () => toast.success("Notificações marcadas como lidas.") })}
      />
    </section>
  );
}
